import { useState, useEffect } from 'react';
import { DTVendable } from '@rabbit/data/types';
import { MagnifyingGlassIcon, FunnelIcon, ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import { QueueListIcon, Squares2X2Icon } from '@heroicons/react/24/solid';

export interface SearchFilterProps {
  data?: DTVendable[];
  onFilterChange: (filteredData: DTVendable[]) => void;
  className?: string;
  viewType?: 'grid' | 'list';
  onViewTypeChange?: (viewType: 'grid' | 'list') => void;
}

export function SearchFilter({
  data,
  onFilterChange,
  className = '',
  viewType = 'grid',
  onViewTypeChange
}: SearchFilterProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [sortOption, setSortOption] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  // Get unique brands for filter
  const brands = data
    ? [...new Set(data.map((item) => item.brand))].filter(Boolean)
    : [];

  // Filter and sort data based on search term, selected brand, and sort option
  useEffect(() => {
    if (!data) return;

    let filtered = [...data];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item?.mpn?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply brand filter
    if (selectedBrand) {
      filtered = filtered.filter((item) => item.brand === selectedBrand);
    }

    // Apply sorting
    if (sortOption) {
      switch (sortOption) {
        case 'name-asc':
          filtered.sort((a, b) => a.title.localeCompare(b.title));
          break;
        case 'name-desc':
          filtered.sort((a, b) => b.title.localeCompare(a.title));
          break;
        // Add more sort options if needed
      }
    }

    onFilterChange(filtered);
  }, [data, searchTerm, selectedBrand, sortOption, onFilterChange]);

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedBrand('');
    setSortOption('');
  };

  return (
    <div className={`${className} bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <FunnelIcon className="h-5 w-5 mr-2 text-primary-600" />
            Filter Products
          </h3>
          <div className="flex items-center">
            {onViewTypeChange && (
              <div className="flex items-center bg-white rounded-lg border border-gray-200 shadow-sm p-1 mr-3">
                <button
                  onClick={() => onViewTypeChange('grid')}
                  className={`p-2 rounded-md transition-all duration-200 ${
                    viewType === 'grid'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                  aria-label="Grid view"
                  title="Grid view"
                >
                  <Squares2X2Icon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => onViewTypeChange('list')}
                  className={`p-2 rounded-md transition-all duration-200 ${
                    viewType === 'list'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                  aria-label="List view"
                  title="List view"
                >
                  <QueueListIcon className="h-5 w-5" />
                </button>
              </div>
            )}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm text-primary-600 hover:text-primary-800 font-medium transition-colors duration-200"
            >
              {isExpanded ? 'Collapse' : 'Expand'} Filters
            </button>
          </div>
        </div>

        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by name or MPN..."
            className="pl-10 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 transition-all duration-300 ${isExpanded ? 'max-h-96' : 'max-h-0 overflow-hidden'}`}>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 flex items-center">
              <span className="w-2 h-2 bg-primary-600 rounded-full mr-2"></span>
              Brand
            </label>
            <div className="relative">
              <select
                className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none bg-gray-50 hover:bg-white transition-all duration-200"
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
              >
                <option value="">All Brands</option>
                {brands.map((brand, index) => (
                  <option key={index} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 flex items-center">
              <span className="w-2 h-2 bg-primary-600 rounded-full mr-2"></span>
              Sort By
            </label>
            <div className="relative">
              <select
                className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none bg-gray-50 hover:bg-white transition-all duration-200"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
              >
                <option value="">Default</option>
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <ArrowsUpDownIcon className="h-4 w-4" />
              </div>
            </div>
          </div>
        </div>

        {(searchTerm || selectedBrand || sortOption) && (
          <div className={`flex justify-end mt-4 ${isExpanded ? '' : 'hidden'}`}>
            <button
              onClick={resetFilters}
              className="text-sm text-primary-600 hover:text-primary-800 font-medium transition-colors duration-200"
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>

      {(searchTerm || selectedBrand || sortOption) && (
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-600">Active filters:</span>
            {searchTerm && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                Search: {searchTerm}
              </span>
            )}
            {selectedBrand && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                Brand: {selectedBrand}
              </span>
            )}
            {sortOption && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                Sort: {sortOption === 'name-asc' ? 'Name (A-Z)' : 'Name (Z-A)'}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchFilter;
